import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, MapPin, Star, Lock, MessageCircle, X } from "lucide-react";
import { resolveImg } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";

export function formatDateBadge(iso) {
  const d = new Date(iso + "T00:00:00");
  return {
    day: d.toLocaleDateString("en-US", { day: "2-digit" }),
    mon: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
  };
}

// Turn rich-text HTML into a clean plain-text excerpt (strips tags, decodes entities like &nbsp;).
function richTextToPlain(html) {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || "").replace(/\s+/g, " ").trim();
}

export function EventCard({ event, index = 0 }) {
  const navigate = useNavigate();
  const { t, tl } = useLang();
  const [showStudents, setShowStudents] = useState(false);
  const d = formatDateBadge(event.date);
  const img = resolveImg(event.image_url || event.flyer_url);
  const isLocked = event.students_only && event.locked;

  const waNumber = (event.students_contact_number || t("contact.whatsapp") || "").replace(/[^0-9]/g, "");
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi! I'd like the access link for the students-only event "${event.title}".`)}`;

  const handleClick = () => {
    if (isLocked) { setShowStudents(true); return; }
    navigate(`/events/${event.slug || event.id}`);
  };

  return (
    <>
      <motion.button
        whileHover={{ y: -6 }}
        transition={{ type: "spring", stiffness: 300, damping: 24 }}
        data-testid={`event-card-${event.id}`}
        onClick={handleClick}
        className="group text-left w-full rounded-3xl overflow-hidden bg-white border border-[#0F172A]/5 shadow-[0_8px_30px_rgba(15,23,42,0.05)] flex flex-col"
      >
        <div className="relative aspect-[4/3] bg-[#E8E6E1] overflow-hidden">
          {img ? (
            <img src={img} alt={event.title} className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105 ${isLocked ? "blur-[14px] scale-110" : ""}`} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#BE923C]/30"><Star size={48} /></div>
          )}
          <div className="absolute top-4 left-4 flex flex-col items-center justify-center rounded-2xl bg-white shadow-md w-14 py-2">
            <span className="text-[10px] font-bold tracking-widest text-[#BE923C]">{d.mon}</span>
            <span className="font-serif text-2xl leading-none text-[#0F172A]">{d.day}</span>
          </div>
          {event.category && (
            <span className="absolute top-4 right-4 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-xs font-semibold text-[#0F172A]">
              {event.category}
            </span>
          )}
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <h3 className="font-serif text-xl sm:text-2xl leading-tight">{tl(event, "title")}</h3>
          <div className="mt-3 flex flex-col gap-1.5 text-sm text-[#475569]">
            {event.time && <span className="inline-flex items-center gap-2"><Clock size={15} className="text-[#BE923C]" /> {tl(event, "time")}</span>}
            {event.location && !event.hide_location && !isLocked && <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-[#BE923C]" /> {tl(event, "location")}</span>}
          </div>
          {!isLocked && tl(event, "description") && (
            <p className="mt-3 text-sm text-[#475569] leading-relaxed line-clamp-2">{richTextToPlain(tl(event, "description"))}</p>
          )}
          {isLocked && (
            <span data-testid={`event-students-badge-${event.id}`} className="mt-3 self-start inline-flex items-center gap-1.5 rounded-full bg-[#0F172A]/8 text-[#0F172A] px-3 py-1 text-xs font-semibold">
              <Lock size={13} className="text-[#BE923C]" /> <EditableText k="card.students_only" as="span" />
            </span>
          )}
          {event.sold_out && !isLocked && (
            <span data-testid={`event-sold-out-badge-${event.id}`} className="mt-3 self-start inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-600 px-3 py-1 text-xs font-semibold">
              {t("card.sold_out")}
            </span>
          )}
          {event.no_reservation && !isLocked && (
            <span data-testid={`event-no-rsvp-badge-${event.id}`} className="mt-3 self-start inline-flex items-center gap-1.5 rounded-full bg-[#25D366]/12 text-[#1a8f4a] px-3 py-1 text-xs font-semibold">
              <EditableText k="card.no_rsvp" as="span" />
            </span>
          )}
          <span className="mt-4 inline-flex items-center text-sm font-semibold text-[#BE923C] group-hover:gap-2 gap-1 transition-[gap]">
            {isLocked ? t("card.students_cta") : (t(event.no_reservation ? "card.view" : "card.view_rsvp") + " →")}
          </span>
        </div>
      </motion.button>

      <AnimatePresence>
        {showStudents && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={`students-modal-${event.id}`}>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[#0F172A]/60 backdrop-blur-sm"
              onClick={() => setShowStudents(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 320, damping: 26 }}
              className="relative w-full max-w-md rounded-3xl bg-white p-7 sm:p-8 shadow-2xl text-center"
            >
              <button type="button" onClick={() => setShowStudents(false)} data-testid="students-modal-close"
                className="absolute top-4 end-4 w-9 h-9 rounded-full hover:bg-[#F1F0EC] flex items-center justify-center text-[#475569] transition-colors">
                <X size={18} />
              </button>
              <span className="mx-auto w-14 h-14 rounded-full bg-[#BE923C]/12 flex items-center justify-center text-[#BE923C]"><Lock size={26} /></span>
              <h3 className="mt-4 font-serif text-2xl"><EditableText k="eventdetail.students_title" as="span" /></h3>
              <p className="mt-2 text-[#475569] leading-relaxed"><EditableText k="eventdetail.students_desc" as="span" /></p>
              <a
                data-testid="students-modal-whatsapp"
                href={waUrl}
                target="_blank" rel="noopener noreferrer"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-7 py-3.5 transition-colors"
              >
                <MessageCircle size={18} /> <EditableText k="eventdetail.students_wa" as="span" />
              </a>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
